import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

export default function TermsConditions() {
  return (
    <div className="min-h-screen bg-[#0d1218] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <FileText className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white font-['Outfit']">Termos e Condições</h1>
              <p className="text-slate-400">Última atualização: 26 de Janeiro de 2025</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="glass-card p-8 space-y-8 text-slate-300 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Disposições Gerais</h2>
            <p>
              Os presentes Termos e Condições ("Termos") regulam a utilização da plataforma online da 
              SGFI Capital, Smart Global Financial Insights, S.A. ("SGFI Capital", "nós" ou 
              "Sociedade"), por parte dos seus utilizadores ("Cliente" ou "Utilizador").
            </p>
            <p className="mt-3">
              Ao registar-se e utilizar os nossos serviços, o Cliente declara ter lido, compreendido e 
              aceite integralmente os presentes Termos, bem como a nossa Política de Privacidade. Caso não 
              concorde com alguma disposição, deverá abster-se de utilizar a plataforma.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Definições</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-white">"Conta":</strong> Conta de utilizador registada na plataforma SGFI Capital;</li>
              <li><strong className="text-white">"Depósito":</strong> Transferência de fundos do Cliente para a sua Conta;</li>
              <li><strong className="text-white">"Levantamento":</strong> Solicitação de transferência de fundos da Conta para uma conta bancária do Cliente;</li>
              <li><strong className="text-white">"Rendimento":</strong> Lucros gerados pela gestão do capital investido;</li>
              <li><strong className="text-white">"Taxa de Rendimento":</strong> Percentagem mensal aplicada ao capital investido;</li>
              <li><strong className="text-white">"Plataforma":</strong> Website e sistema online da SGFI Capital.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. Elegibilidade e Registo</h2>
            <p>Para utilizar os serviços da SGFI Capital, o Cliente deve:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Ter idade igual ou superior a 18 anos;</li>
              <li>Possuir capacidade jurídica plena para contratar;</li>
              <li>Ser residente fiscal em Portugal ou noutro país da União Europeia;</li>
              <li>Fornecer informações verdadeiras, completas e atualizadas;</li>
              <li>Apresentar documento de identificação válido;</li>
              <li>Possuir conta bancária em seu nome.</li>
            </ul>
            <p className="mt-3">
              O registo está sujeito a aprovação pela SGFI Capital, que se reserva o direito de recusar 
              ou cancelar qualquer registo sem necessidade de justificação.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Serviços Prestados</h2>
            <p>A SGFI Capital oferece os seguintes serviços:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Gestão de carteira de investimentos;</li>
              <li>Processamento de depósitos e levantamentos;</li>
              <li>Cálculo e distribuição de rendimentos;</li>
              <li>Acesso a painel de controlo pessoal;</li>
              <li>Relatórios e extratos de conta.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Depósitos</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Os depósitos podem ser efetuados por transferência bancária ou MB Way;</li>
              <li>Os depósitos estão sujeitos a confirmação pela SGFI Capital;</li>
              <li>O prazo de processamento é de até 2 dias úteis após a confirmação da transferência;</li>
              <li>A SGFI Capital reserva-se o direito de solicitar documentação adicional para verificação;</li>
              <li>Depósitos de origem duvidosa serão rejeitados e comunicados às autoridades competentes.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Levantamentos</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>O montante mínimo de levantamento é de €50,00 (cinquenta euros);</li>
              <li>Os pedidos de levantamento estão sujeitos a aprovação;</li>
              <li>O prazo de processamento é de até 5 dias úteis após aprovação;</li>
              <li>Os levantamentos são efetuados exclusivamente para contas bancárias em nome do Cliente;</li>
              <li>A SGFI Capital pode solicitar verificação adicional de identidade para levantamentos;</li>
              <li>A SGFI Capital reserva-se o direito de reter levantamentos em caso de suspeita de fraude ou irregularidade.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">7. Rendimentos e Taxas</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>A taxa de rendimento é definida individualmente para cada Cliente;</li>
              <li>Os rendimentos são calculados mensalmente sobre o saldo da conta;</li>
              <li>Os rendimentos são creditados automaticamente na conta do Cliente;</li>
              <li>A taxa de rendimento pode ser alterada mediante aviso prévio de 30 dias;</li>
              <li>Os rendimentos estão sujeitos a tributação conforme a legislação fiscal aplicável;</li>
              <li>A SGFI Capital não garante rendimentos futuros; os rendimentos passados não constituem garantia de rendimentos futuros.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">8. Obrigações do Cliente</h2>
            <p>O Cliente compromete-se a:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Manter os dados de acesso à conta confidenciais e seguros;</li>
              <li>Notificar imediatamente a SGFI Capital em caso de acesso não autorizado;</li>
              <li>Fornecer informações verdadeiras e atualizadas;</li>
              <li>Não utilizar a plataforma para fins ilícitos ou fraudulentos;</li>
              <li>Cumprir todas as obrigações fiscais e legais aplicáveis;</li>
              <li>Não ceder, transferir ou partilhar o acesso à sua conta com terceiros.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">9. Responsabilidades e Limitações</h2>
            <p>
              A SGFI Capital envidará os melhores esforços para garantir a disponibilidade e segurança 
              da plataforma, contudo:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Não garante que a plataforma estará disponível de forma ininterrupta;</li>
              <li>Não se responsabiliza por perdas resultantes de fatores fora do seu controlo;</li>
              <li>Não se responsabiliza por erros ou atrasos causados por terceiros (bancos, operadores);</li>
              <li>Reserva-se o direito de suspender temporariamente os serviços para manutenção;</li>
              <li>A responsabilidade máxima da SGFI Capital limita-se ao saldo disponível na conta do Cliente.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">10. Prevenção de Branqueamento de Capitais</h2>
            <p>
              Em cumprimento da legislação de prevenção de branqueamento de capitais e financiamento do 
              terrorismo, a SGFI Capital:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Procede à identificação e verificação de todos os Clientes;</li>
              <li>Monitoriza as transações para detetar atividades suspeitas;</li>
              <li>Comunica às autoridades competentes quaisquer operações suspeitas;</li>
              <li>Conserva os registos de transações pelo período legalmente exigido;</li>
              <li>Pode solicitar informação adicional sobre a origem dos fundos.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">11. Proteção de Dados</h2>
            <p>
              O tratamento dos dados pessoais do Cliente é regulado pela nossa Política de Privacidade, 
              que constitui parte integrante destes Termos. Ao aceitar estes Termos, o Cliente confirma 
              ter lido e compreendido a nossa Política de Privacidade.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">12. Propriedade Intelectual</h2>
            <p>
              Todos os conteúdos da plataforma, incluindo mas não limitado a logótipos, textos, gráficos, 
              imagens, software e código, são propriedade da SGFI Capital ou dos seus licenciantes e estão 
              protegidos por direitos de autor e outros direitos de propriedade intelectual. É proibida a 
              reprodução, modificação, distribuição ou utilização comercial sem autorização prévia.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">13. Suspensão e Encerramento</h2>
            <p>A SGFI Capital pode suspender ou encerrar a conta do Cliente:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Por violação destes Termos ou da legislação aplicável;</li>
              <li>Por suspeita de atividade fraudulenta ou irregular;</li>
              <li>Por fornecimento de informações falsas ou enganosas;</li>
              <li>Por inatividade prolongada da conta (12 meses sem movimentos);</li>
              <li>Por ordem judicial ou de autoridade reguladora.</li>
            </ul>
            <p className="mt-3">
              O Cliente pode solicitar o encerramento da sua conta a qualquer momento, mediante pedido 
              escrito. O saldo remanescente será transferido para a conta bancária do Cliente no prazo 
              de 30 dias úteis.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">14. Alterações aos Termos</h2>
            <p>
              A SGFI Capital reserva-se o direito de alterar estes Termos a qualquer momento. As alterações 
              serão comunicadas por email e/ou publicadas na plataforma com 30 dias de antecedência. A 
              continuação da utilização da plataforma após a entrada em vigor das alterações constitui 
              aceitação dos novos Termos.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">15. Lei Aplicável e Jurisdição</h2>
            <p>
              Estes Termos são regidos pela lei portuguesa. Qualquer litígio emergente destes Termos será 
              submetido à jurisdição exclusiva dos tribunais portugueses, com expressa renúncia a qualquer 
              outro foro.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">16. Contactos</h2>
            <p>
              Para questões relacionadas com estes Termos, o Cliente pode contactar-nos através de:
            </p>
            <p className="mt-3">
              <strong className="text-white">Email:</strong> capitalsgfi@gmail.com<br />
              <strong className="text-white">Horário de atendimento:</strong> Dias úteis, das 9h00 às 18h00
            </p>
          </section>

          <section className="border-t border-white/10 pt-6">
            <p className="text-sm text-slate-400">
              Estes Termos e Condições entram em vigor a 26 de Janeiro de 2025 e substituem todas as 
              versões anteriores. Ao criar uma conta ou utilizar os serviços da SGFI Capital, o Cliente 
              confirma que leu, compreendeu e aceita integralmente estes Termos.
            </p>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center text-sm">
          <Link to="/privacy" className="text-blue-400 hover:text-blue-300">Política de Privacidade</Link>
          <span className="text-slate-600">|</span>
          <Link to="/cookies" className="text-blue-400 hover:text-blue-300">Política de Cookies</Link>
          <span className="text-slate-600">|</span>
          <Link to="/" className="text-blue-400 hover:text-blue-300">Voltar ao Login</Link>
        </div>
      </div>
    </div>
  );
}
